/**
 * The five Alliance apps, shared by the App Launcher, the Sign In page,
 * the System Screens and the Footer.
 *
 * Values come from the Claude Design "Unified Alliance App System" (App
 * Bar System spec, Oct 1 2026).
 */

export type AllianceAppKey =
  | 'arc'
  | 'contracting'
  | 'leads'
  | 'events'
  | 'university';

export interface AllianceApp {
  key: AllianceAppKey;
  /** Short name shown in the launcher, links rows and eyebrows */
  name: string;
  /** Eyebrow on the Sign In page and System Screens */
  fullName: string;
  url: string;
  /** One-line description shown in the App Launcher */
  description: string;
  /** theme appBar.main */
  bar: string;
  /** The app's accent (secondary) color */
  accent: string;
  /** The accent adjusted for contrast on the bar: agent number, active nav, launcher tile icon */
  accentOnBar: string;
  mode: 'light' | 'dark';
  /** background.default / background.paper */
  background: { default: string; paper: string };
  layout: 'sidebar' | 'top-nav';
}

/** Launcher, links-row and footer order */
export const ALLIANCE_APP_ORDER: AllianceAppKey[] = [
  'arc',
  'contracting',
  'leads',
  'events',
  'university',
];

export const ALLIANCE_APPS: Record<AllianceAppKey, AllianceApp> = {
  arc: {
    key: 'arc',
    name: 'ARC',
    fullName: 'The Alliance Resource Center',
    url: 'https://arc.naaleads.com',
    description: 'Your business dashboard',
    bar: '#090a0b',
    accent: '#2196f3',
    accentOnBar: '#00baff',
    mode: 'dark',
    background: { default: '#161718', paper: '#212121' },
    layout: 'top-nav',
  },
  contracting: {
    key: 'contracting',
    name: 'Contracting',
    fullName: 'The Alliance Contracting',
    url: 'https://thealliancecontracting.com',
    description: 'Contracts & writing numbers',
    bar: '#03182E',
    accent: '#00C868',
    accentOnBar: '#00C868',
    mode: 'light',
    background: { default: '#fcfcfc', paper: '#ffffff' },
    layout: 'sidebar',
  },
  leads: {
    key: 'leads',
    name: 'Leads',
    fullName: 'The Alliance Leads',
    url: 'https://theallianceleads.com',
    description: 'Buy & manage leads',
    bar: '#001D22',
    accent: '#0094FF',
    accentOnBar: '#33aaff',
    mode: 'light',
    background: { default: '#fcfcfc', paper: '#ffffff' },
    layout: 'sidebar',
  },
  events: {
    key: 'events',
    name: 'Events',
    fullName: 'The Alliance Events',
    url: 'https://theallianceevents.com',
    description: 'Events, trips & tickets',
    bar: '#212121',
    accent: '#D32F2F',
    accentOnBar: '#ff6f60',
    mode: 'light',
    background: { default: '#fafafa', paper: '#ffffff' },
    layout: 'top-nav',
  },
  university: {
    key: 'university',
    name: 'University',
    fullName: 'The Alliance University',
    url: 'https://naauniversity.com',
    description: 'Training & courses',
    bar: '#CE0E2D',
    accent: '#ffffff',
    accentOnBar: '#ffffff',
    mode: 'dark',
    background: { default: '#1E2124', paper: '#3D4349' },
    layout: 'top-nav',
  },
};

/**
 * The collapsible Integrity group at the bottom of the App Launcher.
 * These open in a new tab.
 */
export const INTEGRITY_LINKS = [
  {
    name: 'Register',
    description: 'Create your Integrity account',
    href: 'https://integrity.com/lifecenterthealliance/',
  },
  {
    name: 'Quotes & E-Apps',
    description: 'Run quotes and submit applications',
    href: 'https://integrity.com/',
  },
  {
    name: 'Integrity Lead Center',
    description: 'Get leads from Integrity',
    href: 'https://www.integrityleadcenter.com/login',
  },
];

/**
 * The recruiting link shown in the user menu (RecruitLink).
 * The agent number decides whose upline the new agent is placed under.
 */
export const buildRecruitLink = (params: { agentNumber: string }) =>
  `https://thealliancecontracting.com/sign-up/the-alliance?RecAgt=${encodeURIComponent(
    params.agentNumber,
  )}`;
